import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Phone, MapPin, Clock, Globe, Mail, Search, Recycle, Cross, Building2,
  Users, Siren, Star, ExternalLink, ChevronRight, Loader2, HeartPulse, ShieldAlert,
} from "lucide-react";

const SITE = (typeof window !== "undefined" ? window.location.origin : "");

const SECTIONS = [
  { id: "urgences", label: "Numéros utiles", icon: Siren },
  { id: "services", label: "Services publics", icon: Building2 },
  { id: "pharmacies", label: "Pharmacies", icon: Cross },
  { id: "dechetteries", label: "Déchetteries", icon: Recycle },
  { id: "associations", label: "Associations", icon: Users },
];

const mapsHref = (parts) =>
  `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(parts.filter(Boolean).join(" "))}`;

const telHref = (n) => `tel:${(n || "").replace(/\s+/g, "")}`;

// Pastille d'action réutilisable (téléphone, itinéraire, site, e-mail)
const Action = ({ href, icon: Icon, children, testid, external }) => (
  <a
    href={href}
    data-testid={testid}
    {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
    className="inline-flex items-center gap-1.5 rounded-full border border-[#E05638]/30 bg-[#E05638]/5 px-3 py-1.5 text-sm font-medium text-[#B8431F] transition-colors hover:bg-[#E05638] hover:text-white"
  >
    <Icon className="h-3.5 w-3.5" />
    {children}
  </a>
);

export default function GuidePratique() {
  const [overview, setOverview] = useState(null);
  const [active, setActive] = useState("urgences");

  useSeo({
    title: "Guide pratique de Chartres & de l'Eure-et-Loir — numéros utiles, services publics, pharmacies",
    description:
      "Le guide pratique des habitants d'Eure-et-Loir (28) : numéros d'urgence, services publics, pharmacies et pharmacie de garde, déchetteries et associations locales. Mis à jour automatiquement — à garder en favori.",
    canonicalPath: "/guide-pratique",
    image: "/og-image.jpg",
    keywords:
      "guide pratique Chartres, numéros utiles Eure-et-Loir, pharmacie de garde 28, services publics Chartres, déchetteries Eure-et-Loir, associations Chartres, mairie Eure-et-Loir",
  });

  useEffect(() => {
    api.get("/guide/overview").then((r) => setOverview(r.data)).catch(() => {});
  }, []);

  // Données structurées JSON-LD (SEO)
  useEffect(() => {
    const ld = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          name: "Guide pratique de Chartres & de l'Eure-et-Loir",
          url: `${SITE}/guide-pratique`,
          description:
            "Numéros utiles, services publics, pharmacies, déchetteries et associations d'Eure-et-Loir (28).",
          inLanguage: "fr-FR",
          isPartOf: { "@type": "WebSite", name: "PROXIreduc-28", url: SITE },
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Accueil", item: `${SITE}/` },
            { "@type": "ListItem", position: 2, name: "Guide pratique", item: `${SITE}/guide-pratique` },
          ],
        },
        ...(overview?.numeros_utiles || []).map((g) => ({
          "@type": "ItemList",
          name: g.groupe,
          itemListElement: g.items.map((it, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: `${it.nom} — ${it.numero}`,
          })),
        })),
      ],
    };
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.setAttribute("data-guide-ld", "1");
    el.text = JSON.stringify(ld);
    document.head.appendChild(el);
    return () => { document.head.querySelectorAll('[data-guide-ld]').forEach((n) => n.remove()); };
  }, [overview]);

  // Observe la section visible pour la navigation ancrée
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => { if (e.isIntersecting) setActive(e.target.id); });
      },
      { rootMargin: "-30% 0px -65% 0px" }
    );
    SECTIONS.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) window.scrollTo({ top: el.offsetTop - 120, behavior: "smooth" });
  };

  const updated = overview?.updated_at
    ? new Date(overview.updated_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })
    : null;

  return (
    <div className="min-h-screen bg-[#FAF8F5]" data-testid="guide-pratique-page">
      {/* Hero */}
      <header className="relative overflow-hidden bg-[#1C2B33] text-white">
        <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)", backgroundSize: "22px 22px" }} />
        <div className="relative mx-auto max-w-5xl px-5 py-14 sm:py-20">
          <span className="inline-flex items-center gap-2 rounded-full bg-[#E05638]/20 px-3 py-1 text-sm font-semibold text-[#F6A58A]">
            <Star className="h-3.5 w-3.5" /> À garder en favori
          </span>
          <h1 className="mt-4 text-4xl font-black leading-tight sm:text-5xl lg:text-6xl">
            Le guide pratique de Chartres<br className="hidden sm:block" /> & de l'Eure-et-Loir
          </h1>
          <p className="mt-4 max-w-2xl text-base text-slate-300 sm:text-lg">
            Toutes les ressources essentielles des habitants du 28 : numéros d'urgence, services publics,
            pharmacies, déchetteries et associations. Mis à jour automatiquement depuis les données officielles.
          </p>
          {updated && (
            <p className="mt-3 text-sm text-slate-400" data-testid="guide-updated">
              Données officielles mises à jour le {updated}
            </p>
          )}
        </div>
      </header>

      {/* Navigation ancrée (sticky) */}
      <nav className="sticky top-16 z-20 border-b border-slate-200 bg-white/90 backdrop-blur-md">
        <div className="mx-auto flex max-w-5xl gap-1 overflow-x-auto px-3 py-2">
          {SECTIONS.map((s) => (
            <button
              key={s.id}
              onClick={() => scrollTo(s.id)}
              data-testid={`guide-nav-${s.id}`}
              className={`flex shrink-0 items-center gap-1.5 rounded-full px-3.5 py-2 text-sm font-semibold transition-colors ${
                active === s.id ? "bg-[#E05638] text-white" : "text-slate-600 hover:bg-slate-100"
              }`}
            >
              <s.icon className="h-4 w-4" /> {s.label}
            </button>
          ))}
        </div>
      </nav>

      <main className="mx-auto max-w-5xl space-y-16 px-5 py-12">
        <UrgencesSection overview={overview} />
        <ServicesSection />
        <PharmaciesSection garde={overview?.pharma_garde} />
        <DechetteriesSection />
        <AssociationsSection />
      </main>
    </div>
  );
}

const SectionTitle = ({ icon: Icon, children, sub }) => (
  <div className="mb-6">
    <h2 className="flex items-center gap-2.5 text-2xl font-extrabold text-[#1C2B33] sm:text-3xl">
      <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#E05638]/10 text-[#E05638]">
        <Icon className="h-5 w-5" />
      </span>
      {children}
    </h2>
    {sub && <p className="mt-2 text-sm text-slate-500 sm:text-base">{sub}</p>}
  </div>
);

/* ---------------- Numéros utiles ---------------- */
function UrgencesSection({ overview }) {
  const groupes = overview?.numeros_utiles || [];
  const icons = { Urgences: ShieldAlert, Santé: HeartPulse, "Écoute & protection": Users };
  return (
    <section id="urgences" className="scroll-mt-24">
      <SectionTitle icon={Siren} sub="Numéros nationaux d'urgence et d'écoute — disponibles partout, gratuits sauf indication.">
        Numéros utiles & urgences
      </SectionTitle>
      <div className="grid gap-5 md:grid-cols-3">
        {groupes.map((g) => {
          const GIcon = icons[g.groupe] || Phone;
          return (
            <div key={g.groupe} className="min-w-0 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="mb-3 flex items-center gap-2 font-bold text-[#1C2B33]">
                <GIcon className="h-4 w-4 text-[#E05638]" /> {g.groupe}
              </div>
              <ul className="space-y-3">
                {g.items.map((it) => (
                  <li key={it.nom} className="flex min-w-0 items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="break-words text-sm font-medium text-slate-700">{it.nom}</div>
                      {it.note && <div className="text-xs text-slate-400">{it.note}</div>}
                    </div>
                    <a
                      href={telHref(it.numero)}
                      data-testid={`guide-num-${it.numero}`}
                      className="shrink-0 rounded-lg bg-[#1C2B33] px-3 py-1.5 text-sm font-black text-white transition-colors hover:bg-[#E05638]"
                    >
                      {it.numero}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </section>
  );
}

/* ---------------- Services publics ---------------- */
function ServicesSection() {
  const [filtres, setFiltres] = useState({ types: [], communes: [] });
  const [type, setType] = useState("");
  const [commune, setCommune] = useState("");
  const [q, setQ] = useState("");
  const [data, setData] = useState({ results: [], total: 0, page: 1, pages: 1 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => { api.get("/guide/services-publics/filtres").then((r) => setFiltres(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    setLoading(true);
    const p = new URLSearchParams();
    if (type) p.set("type", type);
    if (commune) p.set("commune", commune);
    if (q) p.set("q", q);
    p.set("page", page);
    const t = setTimeout(() => {
      api.get(`/guide/services-publics?${p.toString()}`)
        .then((r) => setData(r.data))
        .catch(() => setData({ results: [], total: 0, page: 1, pages: 1 }))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [type, commune, q, page]);

  useEffect(() => { setPage(1); }, [type, commune, q]);

  return (
    <section id="services" className="scroll-mt-24">
      <SectionTitle icon={Building2} sub="Mairies, préfecture, CAF, impôts, France Travail, gendarmeries… de tout l'Eure-et-Loir.">
        Services publics
      </SectionTitle>

      <div className="mb-5 flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un service ou une commune…"
            className="pl-9" data-testid="guide-services-search" />
        </div>
        <Input list="guide-communes" value={commune} onChange={(e) => setCommune(e.target.value)}
          placeholder="Commune" className="sm:w-56" data-testid="guide-services-commune" />
        <datalist id="guide-communes">
          {filtres.communes.map((c) => <option key={c} value={c} />)}
        </datalist>
      </div>

      <div className="mb-5 flex flex-wrap gap-2">
        <FilterChip active={!type} onClick={() => setType("")}>Tous</FilterChip>
        {filtres.types.map((t) => (
          <FilterChip key={t} active={type === t} onClick={() => setType(t)}>{t}</FilterChip>
        ))}
      </div>

      {loading ? <Loading /> : (
        <>
          <p className="mb-4 text-sm text-slate-500" data-testid="guide-services-count">{data.total} service(s) trouvé(s)</p>
          <div className="grid gap-4 md:grid-cols-2">
            {data.results.map((s) => (
              <PlaceCard key={s.id} name={s.nom} badge={s.type_label}
                lines={[s.adresse, [s.code_postal, s.commune].filter(Boolean).join(" ")].filter(Boolean)}
                hours={s.horaires} phone={s.telephone} site={s.site} email={s.email}
                maps={mapsHref([s.nom, s.adresse, s.code_postal, s.commune])} testid="guide-service-card" />
            ))}
          </div>
          {!data.results.length && <Empty />}
          <Pager page={page} pages={data.pages} onPage={setPage} prefix="guide-services" />
        </>
      )}
    </section>
  );
}

/* ---------------- Pharmacies ---------------- */
function PharmaciesSection({ garde }) {
  const [q, setQ] = useState("");
  const [commune, setCommune] = useState("");
  const [data, setData] = useState({ results: [], total: 0, page: 1, pages: 1 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (commune) p.set("commune", commune);
    p.set("page", page);
    const t = setTimeout(() => {
      api.get(`/guide/pharmacies?${p.toString()}`)
        .then((r) => setData(r.data))
        .catch(() => setData({ results: [], total: 0, pages: 1 }))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q, commune, page]);

  useEffect(() => { setPage(1); }, [q, commune]);

  return (
    <section id="pharmacies" className="scroll-mt-24">
      <SectionTitle icon={Cross} sub="Annuaire officiel des pharmacies d'Eure-et-Loir.">Pharmacies</SectionTitle>

      {/* Carte pharmacie de garde (service officiel 3237) */}
      {garde && (
        <div className="mb-6 flex flex-col items-start gap-4 rounded-2xl border-2 border-[#E05638]/30 bg-gradient-to-br from-[#E05638]/5 to-transparent p-5 sm:flex-row sm:items-center sm:justify-between" data-testid="guide-pharma-garde">
          <div>
            <h3 className="flex items-center gap-2 text-lg font-extrabold text-[#1C2B33]">
              <HeartPulse className="h-5 w-5 text-[#E05638]" /> {garde.titre}
            </h3>
            <p className="mt-1 max-w-xl text-sm text-slate-600">{garde.message}</p>
            <p className="mt-1 text-xs font-semibold text-[#B8431F]">{garde.urgence}</p>
          </div>
          <div className="flex shrink-0 gap-2">
            <a href={telHref(garde.tel)} data-testid="guide-garde-call"
              className="rounded-xl bg-[#E05638] px-4 py-2.5 font-bold text-white transition-transform hover:scale-105">
              Appeler le {garde.tel}
            </a>
            <a href={garde.url} target="_blank" rel="noopener noreferrer" data-testid="guide-garde-site"
              className="inline-flex items-center gap-1.5 rounded-xl border border-[#1C2B33]/20 px-4 py-2.5 font-bold text-[#1C2B33] hover:bg-slate-50">
              3237.fr <ExternalLink className="h-4 w-4" />
            </a>
          </div>
        </div>
      )}

      <div className="mb-5 flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nom de pharmacie ou commune…"
            className="pl-9" data-testid="guide-pharma-search" />
        </div>
        <Input value={commune} onChange={(e) => setCommune(e.target.value)} placeholder="Commune"
          className="sm:w-56" data-testid="guide-pharma-commune" />
      </div>

      {loading ? <Loading /> : (
        <>
          <p className="mb-4 text-sm text-slate-500">{data.total} pharmacie(s)</p>
          <div className="grid gap-4 md:grid-cols-2">
            {data.results.map((p, i) => (
              <PlaceCard key={p.siren || i} name={p.nom} badge="Pharmacie"
                lines={[p.adresse || [p.code_postal, p.commune].filter(Boolean).join(" ")].filter(Boolean)}
                maps={mapsHref([p.nom, p.adresse, p.code_postal, p.commune])} testid="guide-pharma-card" />
            ))}
          </div>
          {!data.results.length && <Empty />}
          <Pager page={page} pages={data.pages} onPage={setPage} prefix="guide-pharma" />
        </>
      )}
    </section>
  );
}

/* ---------------- Déchetteries ---------------- */
function DechetteriesSection() {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(() => {
      api.get(`/guide/dechetteries${q ? `?q=${encodeURIComponent(q)}` : ""}`)
        .then((r) => setItems(r.data.results || []))
        .catch(() => setItems([]))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <section id="dechetteries" className="scroll-mt-24">
      <SectionTitle icon={Recycle} sub="Localisation des déchetteries du département (source ADEME).">Déchetteries</SectionTitle>
      <div className="relative mb-5 max-w-md">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher par commune ou adresse…"
          className="pl-9" data-testid="guide-dechet-search" />
      </div>
      {loading ? <Loading /> : (
        <div className="grid gap-4 md:grid-cols-2">
          {items.map((d, i) => (
            <PlaceCard key={d.code_service || i} name={d.nom} badge="Déchetterie"
              lines={[d.commune, d.exploitant].filter(Boolean)}
              maps={d.lat && d.lng ? `https://www.google.com/maps/dir/?api=1&destination=${d.lat},${d.lng}` : mapsHref([d.nom, d.commune])}
              testid="guide-dechet-card" />
          ))}
          {!items.length && <Empty />}
        </div>
      )}
    </section>
  );
}

/* ---------------- Associations (live) ---------------- */
function AssociationsSection() {
  const [q, setQ] = useState("");
  const [data, setData] = useState({ results: [], total: 0, page: 1, pages: 1 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    setLoading(true); setTouched(true);
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    p.set("page", page);
    const t = setTimeout(() => {
      api.get(`/guide/associations?${p.toString()}`)
        .then((r) => setData(r.data))
        .catch(() => setData({ results: [], total: 0, pages: 1 }))
        .finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(t);
  }, [q, page]);

  useEffect(() => { setPage(1); }, [q]);

  return (
    <section id="associations" className="scroll-mt-24">
      <SectionTitle icon={Users} sub="Les associations déclarées d'Eure-et-Loir — recherche en direct, toujours à jour.">
        Associations locales
      </SectionTitle>
      <div className="relative mb-5 max-w-md">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher une association (nom, commune)…"
          className="pl-9" data-testid="guide-assoc-search" />
      </div>
      {loading ? <Loading /> : (
        <>
          {touched && <p className="mb-4 text-sm text-slate-500">Associations déclarées en Eure-et-Loir (siège dans le 28)</p>}
          <div className="grid gap-4 md:grid-cols-2">
            {data.results.map((a) => (
              <div key={a.siren} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="guide-assoc-card">
                <div className="mb-1 inline-block rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-500">
                  Association {a.sigle ? `· ${a.sigle}` : ""}
                </div>
                <h3 className="font-bold text-[#1C2B33]">{a.nom}</h3>
                {(a.adresse || a.commune) && (
                  <p className="mt-1 flex items-start gap-1.5 text-sm text-slate-500">
                    <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    <span>{a.adresse || [a.code_postal, a.commune].filter(Boolean).join(" ")}</span>
                  </p>
                )}
                {a.fiche_url && (
                  <div className="mt-3">
                    <Action href={a.fiche_url} icon={ExternalLink} external testid="guide-assoc-fiche">Fiche officielle</Action>
                  </div>
                )}
              </div>
            ))}
          </div>
          {!data.results.length && <Empty label="Saisissez un nom d'association pour lancer la recherche." />}
          <Pager page={page} pages={Math.min(data.pages, 40)} onPage={setPage} prefix="guide-assoc" />
        </>
      )}
    </section>
  );
}

/* ---------------- Composants partagés ---------------- */
function PlaceCard({ name, badge, lines = [], hours, phone, site, email, maps, testid }) {
  return (
    <div className="flex flex-col rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-shadow hover:shadow-md" data-testid={testid}>
      {badge && <div className="mb-1 inline-block w-fit rounded-full bg-[#E05638]/10 px-2.5 py-0.5 text-xs font-semibold text-[#B8431F]">{badge}</div>}
      <h3 className="font-bold text-[#1C2B33]">{name}</h3>
      {lines.map((l, i) => (
        <p key={i} className="mt-1 flex items-start gap-1.5 text-sm text-slate-500">
          {i === 0 && <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0" />}
          <span className={i === 0 ? "" : "pl-5"}>{l}</span>
        </p>
      ))}
      {hours && (
        <p className="mt-2 flex items-start gap-1.5 text-sm text-slate-600">
          <Clock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#E05638]" /> <span>{hours}</span>
        </p>
      )}
      <div className="mt-4 flex flex-wrap gap-2">
        {phone && <Action href={telHref(phone)} icon={Phone} testid="guide-card-call">{phone}</Action>}
        {maps && <Action href={maps} icon={MapPin} external testid="guide-card-maps">Itinéraire</Action>}
        {site && <Action href={site.startsWith("http") ? site : `https://${site}`} icon={Globe} external testid="guide-card-site">Site</Action>}
        {email && <Action href={`mailto:${email}`} icon={Mail} testid="guide-card-email">E-mail</Action>}
      </div>
    </div>
  );
}

const FilterChip = ({ active, onClick, children }) => (
  <button onClick={onClick}
    data-testid={`guide-type-${String(children).toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
    className={`rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors ${
      active ? "bg-[#1C2B33] text-white" : "bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50"
    }`}>
    {children}
  </button>
);

const Loading = () => (
  <div className="flex items-center justify-center py-12 text-slate-400">
    <Loader2 className="h-6 w-6 animate-spin" />
  </div>
);

const Empty = ({ label = "Aucun résultat pour cette recherche." }) => (
  <p className="col-span-full py-8 text-center text-sm text-slate-400">{label}</p>
);

function Pager({ page, pages, onPage, prefix = "guide" }) {
  if (pages <= 1) return null;
  return (
    <div className="mt-8 flex items-center justify-center gap-3">
      <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => onPage(page - 1)} data-testid={`${prefix}-prev`}>
        Précédent
      </Button>
      <span className="text-sm text-slate-500">Page {page} / {pages}</span>
      <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => onPage(page + 1)} data-testid={`${prefix}-next`}>
        Suivant <ChevronRight className="h-4 w-4" />
      </Button>
    </div>
  );
}
